/**
 * Morgenlage — design mockup (task h-cockpit-daily-dashboard-redesign, follow-up #2954).
 *
 * The farmer's daily briefing, maximal "platform concept" variant (Deliverable 0):
 * verdict-first, exceptions before KPIs, ranked teams within Tätigkeit, the merged
 * Ernte × Preis × DB story, dual lineage (source stamp + model tag) on every card.
 * All content is DEMO DATA (fictional leader names, Beispieldaten) — a concept
 * view for design review and selling, NOT wired to any API. Exported from the
 * barrel because the frontend hosts it at /apps/morgenlage (the Cockpit
 * successor page) as a self-labelled Konzeptentwurf.
 *
 * Responsive via container queries: below 860px container width the layout is the
 * phone column; above it the desktop composition — two-zone grid (main + rail),
 * exception cards 2-up, Tätigkeit groups and Sorten as side-by-side columns, and
 * a full-width Woche & Saison band. Wrap it in a 390px container for the mobile
 * view regardless of viewport.
 *
 * Round-2 review invariants (inputs/2026-08-11-mockup-review-round2.md): the verdict
 * counts the SAME alert collection the cards render; historical completeness and live
 * signals carry separate freshness; every highlighted number states its threshold or
 * baseline (Mindestpuffer, Korridor-Untergrenze); severity (border) is separate from
 * category (label); model tags hide version numbers behind a "Wie berechnet?" tooltip;
 * forecasts are rounded (no false precision); dismissal is reversible.
 */
import { useMemo, useState } from 'react';
import {
  formatDdMm,
  formatLongDate,
  formatShortDate,
  formatShortDateWithYear,
  formatWeekdayLong,
  formatWeekdayShort,
  morgenlageDates,
} from './dates';
import './Morgenlage.css';

const Stamp = ({ children, title }: { children: React.ReactNode; title?: string }) => (
  <span className="mgl-stamp" title={title}>{children}</span>
);

const ModelTag = ({ children, title }: { children: React.ReactNode; title?: string }) => (
  <span className="mgl-modeltag" title={title}>{children}</span>
);

const Vorl = () => <span className="mgl-vorl">vorläufig</span>;

const TEAM_GROUPS = [
  {
    label: 'Ernte · Erdbeeren', short: 'Ernte', unit: 'kg je Personenstunde',
    rows: [
      { rank: 1, name: 'Team Novak', ak: 31, val: '9,4', delta: '▲ +6 %', tone: 'up' },
      { rank: 2, name: 'Team Weber', ak: 24, val: '8,7', delta: '± 0 %', tone: 'flat' },
      { rank: 3, name: 'Team Lehmann', ak: 18, val: '7,9', delta: '▼ −5 %', tone: 'down' },
    ],
  },
  {
    label: 'Pflanzen', short: 'Pflanzen', unit: 'Pflanzen je Personenstunde',
    rows: [
      { rank: 1, name: 'Team Kowalski', ak: 26, val: '248', delta: '▲ +9 %', tone: 'up' },
      { rank: 2, name: 'Team Ionescu', ak: 22, val: '214', delta: '▲ +3 %', tone: 'up' },
      { rank: 3, name: 'Team Szabó', ak: 19, val: '187', delta: '▼ −4 %', tone: 'down' },
    ],
  },
];

const SORTEN = [
  {
    name: 'Lady Emma', t: '5,2 t', preis: '5,40 €', db: '2,10 €', tone: 'pos',
    spark: '0,20 12,14.9 24,17.4 36,9.7 48,2 60,7.1 72,4.6', dot: { x: 72, y: 4.6 },
    area: '0,20 12,14.9 24,17.4 36,9.7 48,2 60,7.1 72,4.6 72,22 0,22',
    delta: <>im Korridor · &#43;0,22&nbsp;&#8364;/kg ggü. Wochen-Ø</>,
  },
  {
    name: 'Karima', t: '8,1 t', preis: '4,85 €', db: '1,46 €', tone: 'neg',
    spark: '0,10.5 12,6.2 24,8.4 36,4.1 48,2 60,7.3 72,20', dot: { x: 72, y: 20 },
    area: '0,10.5 12,6.2 24,8.4 36,4.1 48,2 60,7.3 72,20 72,22 0,22',
    delta: <>&#8722;12&nbsp;% ggü. Korridor-Untergrenze · &#8722;0,18&nbsp;&#8364;/kg ggü. Wochen-Ø</>,
  },
];

/* Forecast values deliberately rounded to whole tonnes — model output, no false precision.
   The day LABELS are NOT here: they are derived from `now` (dates.ts) and zipped on by
   index below, so the bars, the aria-label and the prose underneath cannot disagree. */
const FORECAST_VALUES = [
  { val: '21', px: 38, peak: false },
  { val: '29', px: 52, peak: true },
  { val: '18', px: 33, peak: false },
  { val: '12', px: 22, peak: false },
];

/* The narrative is "the peak is the last full harvest window BEFORE the rain", so the
   rain day is the day after the peak. Deriving both from the single `peak` flag is what
   keeps the Wetter prose, the Ernte-Ausblick prose and the bars in agreement on any day. */
const PEAK_INDEX = FORECAST_VALUES.findIndex((day) => day.peak);
const RAIN_INDEX = PEAK_INDEX + 1;

export interface MorgenlageProps {
  className?: string;
  /**
   * The clock the page reads itself against. Defaults to the real local time; inject it
   * to pin Storybook and tests. Local time is deliberate — a UTC basis would name the
   * wrong weekday for a German farmer between 00:00 and 02:00 CEST (see dates.ts).
   */
  now?: Date;
}

export function Morgenlage({ className, now }: MorgenlageProps) {
  /* Mobile shows one Tätigkeit at a time (segmented switch); desktop shows both columns. */
  const [teamTab, setTeamTab] = useState(0);

  const dates = useMemo(() => morgenlageDates(now ?? new Date()), [now]);
  const forecast = useMemo(
    () => FORECAST_VALUES.map((value, i) => ({ ...value, date: dates.forecast[i] })),
    [dates],
  );
  const peakDay = forecast[PEAK_INDEX].date;
  const rainDay = forecast[RAIN_INDEX].date;
  const syncStamp = `Synchronisiert am ${formatDdMm(dates.today)} um 05:30`;

  return (
    <div className={`mgl-root${className ? ` ${className}` : ''}`}>
      <div className="mgl-screen">

        <header className="mgl-head">
          <div className="mgl-h1row">
            <h1>Morgenlage</h1>
            <span className="mgl-h1date">{formatLongDate(dates.today)}</span>
          </div>
          <p className="mgl-datenlage mgl-num">
            Tagesabschluss <span className="mgl-ok">vollständig</span> bis {formatShortDate(dates.datenstand)} ·
            Live-Signale: {formatWeekdayShort(dates.today)}, 06:00
          </p>
        </header>

        <section className="mgl-verdict" aria-label="Tagesstatus">
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path d="M9 2 L16.5 15.5 H1.5 Z" fill="var(--color-warning-400)" />
            <rect x="8.2" y="7" width="1.6" height="4.2" rx="0.8" fill="#fff" />
            <circle cx="9" cy="13" r="1" fill="#fff" />
          </svg>
          <div>
            <p className="mgl-verdict-main">1 Priorität heute · 1 Frühwarnung · 2 technische Hinweise</p>
            <p className="mgl-verdict-sub mgl-num">
              Priorität: Karima liegt 1,1 t unter der Korridor-Untergrenze. Ernte, Preise,
              Deckungsbeitrag und Liquidität geprüft.
            </p>
          </div>
        </section>

        <div className="mgl-cols">
          <div className="mgl-main">

            <div className="mgl-beachten">

              <section className="mgl-card mgl-alert">
                <p className="mgl-eyebrow mgl-eyebrow--warn">Auffälligkeit</p>
                <p className="mgl-cardtitle mgl-num"><span className="mgl-entity">Karima</span>: 8,1 t — 1,1 t unter der Untergrenze</p>
                <p className="mgl-alert-ev mgl-num">
                  Korridor (Saisontag-Basis): <b>9,2–10,4 t</b> · <b>−12 %</b> ggü. Untergrenze ·
                  Deckungsbeitrag <b>−0,18 €/kg</b> ggü. Wochen-Ø
                </p>
                <div className="mgl-chips">
                  <span className="mgl-chip mgl-chip--soft mgl-num">Hitzetag am {formatDdMm(dates.datenstand)} · 34 °C · mögliche Ursache</span>
                  <ModelTag title="Hofsync-Modell Ernte-Korridor v0.2 · Wie berechnet?">Ernte-Korridor</ModelTag>
                </div>
                <div className="mgl-cta">
                  <button className="mgl-btn" type="button">Warum liegt Karima unter dem Korridor?</button>
                </div>
              </section>

              <section className="mgl-card mgl-alert mgl-alert--fw">
                <p className="mgl-eyebrow mgl-eyebrow--info">Frühwarnung</p>
                <p className="mgl-cardtitle mgl-num">Liquidität: Engpass am {formatShortDate(dates.engpass)} absehbar</p>
                <p className="mgl-alert-ev mgl-num">
                  Umsatzsteuer (<b>−86 T€</b>) und Löhne (<b>−54 T€</b>) fallen in dieselbe Woche.
                  Erwarteter Tiefststand <b>118 T€</b> · Mindestpuffer <b>150 T€</b> → Lücke <b>32 T€</b>.
                </p>
                <p className="mgl-reco mgl-num"><strong>Empfehlung:</strong> Verpackungs-Einkauf 5 Tage verschieben → erwarteter Puffer <strong>149 T€</strong> am {formatDdMm(dates.engpass)}</p>
                <div className="mgl-chips"><ModelTag title="Hofsync-Modell v0.4 · Wie berechnet?">Liquiditäts-Prognose</ModelTag></div>
                <div className="mgl-cta">
                  <button className="mgl-btn mgl-btn--primary" type="button">Details ansehen</button>
                  <button className="mgl-btn mgl-btn--ghost" type="button">Später erinnern</button>
                </div>
              </section>

            </div>

            <section className="mgl-card" data-testid="mgl-teams">
              <p className="mgl-eyebrow">gestern</p>
              <div className="mgl-cardhead">
                <span className="mgl-cardtitle">Teamleistung</span>
                <Stamp title={syncStamp}>wiberry</Stamp>
              </div>

              <div className="mgl-tseg" role="tablist" aria-label="Tätigkeit wählen">
                {TEAM_GROUPS.map((g, i) => (
                  <button
                    key={g.short}
                    role="tab"
                    aria-selected={i === teamTab}
                    className={`mgl-tseg-btn${i === teamTab ? ' mgl-tseg-btn--active' : ''}`}
                    onClick={() => setTeamTab(i)}
                    type="button"
                  >
                    {g.short}
                  </button>
                ))}
              </div>

              <div className="mgl-tgroups">
                {TEAM_GROUPS.map((g, i) => (
                  <div className={`mgl-tgroup${i === teamTab ? ' mgl-tgroup--active' : ''}`} key={g.label}>
                    <p className="mgl-tgroup-label"><span>{g.label}</span><span>{g.unit}</span></p>
                    {g.rows.map((t) => (
                      <div className="mgl-trow" key={t.name}>
                        <span className={`mgl-rank mgl-rank--${t.rank} mgl-num`}>{t.rank}</span>
                        <span className="mgl-tname">{t.name}<span className="mgl-ak mgl-num">{t.ak} Arbeitskräfte</span></span>
                        <span className="mgl-tval mgl-num">{t.val}</span>
                        <span className="mgl-tdelta"><span className={`mgl-chip mgl-chip--${t.tone} mgl-num`}>{t.delta}</span></span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              <div className="mgl-selfcap mgl-num"><span><b>Musterhof</b> · {formatShortDateWithYear(dates.datenstand)}</span><span>hofsync.de</span></div>
            </section>

            <section className="mgl-card">
              <p className="mgl-eyebrow">gestern</p>
              <div className="mgl-cardhead">
                <span className="mgl-cardtitle">Ernte nach Sorte</span>
                <Stamp title={syncStamp}>wiberry · MyFarm24 · DATEV</Stamp>
              </div>

              <div className="mgl-sorten">
                {SORTEN.map((s) => (
                  <div className="mgl-sorte" key={s.name}>
                    <div className="mgl-sorte-top">
                      <span className="mgl-sorte-name"><span className="mgl-entity">{s.name}</span></span>
                      <span className="mgl-msparkbox">
                        <svg
                          className={`mgl-mspark mgl-mspark--${s.tone}`}
                          width="72" height="22" viewBox="0 0 72 22"
                          aria-label={`${s.name}: Ernte-Trend, letzte 7 Tage`}
                        >
                          <polygon points={s.area} />
                          <polyline points={s.spark} />
                          <circle cx={s.dot.x} cy={s.dot.y} r="2.5" />
                        </svg>
                      </span>
                    </div>
                    <div className="mgl-sorte-figs mgl-num">
                      <div className="mgl-fig"><div className="mgl-fig-v">{s.t}</div><div className="mgl-fig-l">Ernte</div></div>
                      <div className="mgl-fig"><div className="mgl-fig-v">{s.preis}</div><div className="mgl-fig-l" title="kg-gewichteter Durchschnitt">Preis je kg</div></div>
                      <div className="mgl-fig"><div className="mgl-fig-v">{s.db}</div><div className="mgl-fig-l">DB je kg <Vorl /></div></div>
                    </div>
                    <p className={`mgl-sorte-delta mgl-num mgl-sorte-delta--${s.tone}`}>{s.delta}</p>
                  </div>
                ))}
              </div>

              <div className="mgl-channels">
                <p className="mgl-channels-label">Erdbeeren gesamt nach Absatzkanal</p>
                <p className="mgl-channelvals mgl-num"><span>Großhandel <b>4,85 €/kg</b></span><span>Einzelhandel <b>6,70 €/kg</b></span></p>
              </div>
            </section>

            <section className="mgl-card">
              <div className="mgl-cardhead">
                <span className="mgl-cardtitle">Woche &amp; Saison</span>
              </div>
              <div className="mgl-sgrid mgl-num">
                <div className="mgl-scell"><div className="mgl-scell-v">1.412 t</div><div className="mgl-scell-l">Ernte Saison</div><div className="mgl-scell-d mgl-scell-d--pos">+6 % ggü. Vorjahr (Saisontag)</div></div>
                <div className="mgl-scell"><div className="mgl-scell-v">6,9 Mio. €</div><div className="mgl-scell-l">Umsatz Saison</div><div className="mgl-scell-d mgl-scell-d--pos">+4 % ggü. Vorjahr</div></div>
                <div className="mgl-scell"><div className="mgl-scell-v">1,82 € <Vorl /></div><div className="mgl-scell-l">DB je kg · Saison-Ø</div></div>
                <div className="mgl-scell"><div className="mgl-scell-v">38 %</div><div className="mgl-scell-l">Direktvermarktung diese Woche</div><div className="mgl-scell-d mgl-scell-d--pos">Saisonhoch</div></div>
              </div>
            </section>

          </div>
          <div className="mgl-rail">

            <section className="mgl-card">
              <div className="mgl-cardhead">
                <span className="mgl-cardtitle">Kontostand</span>
                <Stamp title={`Kontoauszüge bis ${formatDdMm(dates.bankStand)} verarbeitet`}>Bank · DATEV</Stamp>
              </div>
              <div className="mgl-heroline mgl-num">
                <span className="mgl-hero">742.300 €</span>
                <span className="mgl-chip mgl-chip--up mgl-num">▲ +18,2 T€ ggü. Vortag</span>
              </div>
              <p className="mgl-note mgl-num">Tiefster erwarteter Stand: <strong>118 T€ am {formatDdMm(dates.engpass)}</strong> · Mindestpuffer <strong>150 T€</strong></p>
              <div className="mgl-chips"><ModelTag title="Hofsync-Modell v0.4 · Wie berechnet?">Liquiditäts-Prognose</ModelTag></div>
            </section>

            <section className="mgl-card">
              {/* KW nn is the UPCOMING week — this is next week's recommendation, not
                  a readout of the current one. See dates.ts `nextWeek`. */}
              <p className="mgl-eyebrow">Preis-Empfehlung · KW {dates.nextWeek.week}</p>
              <div className="mgl-cardhead">
                <span className="mgl-cardtitle">Erdbeeren Kl. I · Großhandel</span>
                <Stamp title="Hofsync-Modell v0.3 · Wie berechnet?">Preis-Modell</Stamp>
              </div>
              <div className="mgl-heroline mgl-num">
                <span className="mgl-hero">5,60–5,90 €</span>
                <span className="mgl-chip mgl-chip--soft mgl-num">Ø letzte Woche: 5,35 €</span>
              </div>
              <p className="mgl-note">Basis: eigene Historie · Ernteprognose · Wetter · AMI-Preisspiegel</p>
              <p className="mgl-link">Im Preisspiegel öffnen →</p>
            </section>

            <section className="mgl-card">
              <p className="mgl-eyebrow">heute</p>
              <div className="mgl-cardhead">
                <span className="mgl-cardtitle">Wetter · Region</span>
                <Stamp>DWD</Stamp>
              </div>
              <div className="mgl-wrow mgl-num"><span className="mgl-wtemp">31 °C</span><span className="mgl-wdesc">sonnig, trocken</span></div>
              <div className="mgl-impacts mgl-num">
                <p className="mgl-impact"><strong>Hitzestress 14–17 Uhr</strong> — Ernte-Fenster vor 12 Uhr nutzen</p>
                <p className="mgl-impact">Bewässerungsbedarf <strong>+14 %</strong> ggü. Wochen-Ø</p>
                <p className="mgl-impact">Regen ab {formatWeekdayLong(rainDay)} — <strong>{formatWeekdayShort(peakDay)} als letztes volles Ernte-Fenster</strong> einplanen</p>
              </div>
              <div className="mgl-chips"><ModelTag title="Hofsync-Modell v0.2 · Wie berechnet?">Wetter-Impact</ModelTag></div>
            </section>

            <section className="mgl-card">
              <div className="mgl-cardhead">
                <span className="mgl-cardtitle">Erwartete Ernte · ca.&nbsp;t/Tag</span>
                <Stamp>fruitforecast</Stamp>
              </div>
              <div
                className="mgl-fcbars mgl-num"
                role="img"
                aria-label={`Erwartete Erntemenge ${formatWeekdayLong(forecast[0].date)} bis ${formatWeekdayLong(forecast[forecast.length - 1].date)} in Tonnen, gerundet: ${forecast.map((d) => d.val).join(' · ')}`}
              >
                {forecast.map((d) => (
                  <div className="mgl-fcbar" key={d.date.getTime()}>
                    <span className="mgl-fcval">{d.val}</span>
                    <span className={`mgl-fccol${d.peak ? ' mgl-fccol--peak' : ''}`} style={{ height: `${d.px}px` }} />
                    <span className="mgl-fcday">{formatWeekdayShort(d.date)}</span>
                  </div>
                ))}
              </div>
              <p className="mgl-note"><strong>Erntespitze am {formatWeekdayLong(peakDay)} (ca. {forecast[PEAK_INDEX].val} t)</strong> — letztes volles Ernte-Fenster vor dem Regen am {formatWeekdayLong(rainDay)}.</p>
            </section>

            <section className="mgl-card">
              <div className="mgl-cardhead">
                <span className="mgl-cardtitle">Anlagen &amp; Technik</span>
                <Stamp>Sigrow · Spagnol · via agrirouter</Stamp>
              </div>
              <div className="mgl-arow mgl-num"><span className="mgl-dot mgl-dot--warn" /><span className="mgl-awhat"><b>Bewässerung:</b> 5/6 Sektoren aktiv — <span className="mgl-entity">Sektor 4</span> offline</span><span className="mgl-astatus mgl-astatus--warn">prüfen</span></div>
              <div className="mgl-arow mgl-num"><span className="mgl-dot mgl-dot--warn" /><span className="mgl-awhat"><b>Tunnel-Klima:</b> 11/12 im Zielbereich — <span className="mgl-entity">Tunnel 7</span> VPD über Ziel</span><span className="mgl-astatus mgl-astatus--warn">prüfen</span></div>
              <div className="mgl-arow mgl-num"><span className="mgl-dot mgl-dot--ok" /><span className="mgl-awhat"><b>Brunnen:</b> 84 m³ gestern — im Plan</span><span className="mgl-astatus mgl-astatus--ok">ok</span></div>
            </section>

          </div>
        </div>

        <div className="mgl-bottom">
          <div className="mgl-simchip">
            <span className="mgl-simq mgl-num">
              <span className="mgl-sim-label">Szenario rechnen</span>
              Neuer Folientunnel für 600 T€ — Auswirkung auf Rendite und Liquidität
            </span>
            <span className="mgl-simgo">→</span>
          </div>
        </div>

        <p className="mgl-foot">Hofsync · Konzeptentwurf mit Beispieldaten · berechnet auf Datenstand {formatShortDateWithYear(dates.datenstand)}</p>

      </div>
    </div>
  );
}
